import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Paper, Typography, Tabs, Tab, Box, Select, MenuItem, IconButton, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, useTheme, useMediaQuery } from '@mui/material';
import { ArrowBack } from "@mui/icons-material";
import Grid from '@mui/material/Grid2';
import axios from 'axios';
import DataLoader from '../components/common/DataLoader.js';
import config from '../config.js';
import { formatEuro, formatPct } from '../functions/formatNumber.js';
import { listAnysInversio } from '../functions/common-lists.js';
import monthName from '../functions/month.js';
import HistoricInversioChart from '../components/dashboard-inversio/HistoricInversioChart.js';
import { ActiuCardAnual, MesCardAnual } from '../components/dashboard-inversio/cards/index.js';

const DashboardInversioAnual = () => {

  const { userapp, anyo } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [tabValue, setTabValue] = useState(0);
  const [selectedAssetId, setSelectedAssetId] = useState(null);

  const fetchFunction = (jwtToken, onData, onError, onFinally) => {
    axios.get(`${config.apiBaseUrl}/v1/resumanualinversio/${userapp}/${anyo}`, {
      headers: { Authorization: `${jwtToken}` }
    })
    .then((response) => {
      onData(response);
    })
    .catch((err) => {
      onError(err, 'carregar dades de inversió anual');
    })
    .finally(() => onFinally());
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleYearChange = (year) => {
    setSelectedAssetId(null);
    navigate(`/${userapp}/inversio/${year}`);
  };

  const gotoBack = () => {
    navigate(`/${userapp}/inversio`);
  };

  const handleRowClick = (assetId) => {
    setSelectedAssetId(prev => prev === assetId ? null : assetId);
  };

  const formatAportatRetirat = (aportat, retirat) => {
    const apStr = aportat > 0 ? `+${formatEuro(aportat)}` : '0,00 €';
    const retStr = retirat > 0 ? `-${formatEuro(retirat)}` : '0,00 €';
    return `${apStr} / ${retStr}`;
  };

  return (
    <DataLoader id={anyo} fetchFunction={fetchFunction} goBackLocation={`/${userapp}/inversio`}>
      {(data) => {
        const assets = data.actius || [];
        const months = data.mesos || [];

        const rendimientoTotal = data.rendimientoTotal || 0;
        const colorGP = rendimientoTotal >= 0 ? '#4CAF50' : '#EF4444';
        const colorGPBg = rendimientoTotal >= 0 ? 'rgba(76, 175, 80, 0.12)' : 'rgba(239, 68, 68, 0.12)';

        const baseCapital = (data.saldoInicial || 0) + (data.aportacions || 0);
        const percentatgeRetorn = data.percentatgeRetorn !== undefined && data.percentatgeRetorn !== null
          ? data.percentatgeRetorn
          : (baseCapital > 0 ? (rendimientoTotal / baseCapital) * 100 : 0);

        return (
          <>
            <Grid size={12} sx={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '10px' }}>
              <IconButton onClick={gotoBack}>
                <ArrowBack />
              </IconButton>
              <Typography variant="h5">
                Dashboard Inversió Anual
              </Typography>
              <Select
                value={data.anio || anyo}
                onChange={(e) => handleYearChange(e.target.value)}
                size="small"
                sx={{ ml: 2 }}
              >
                {listAnysInversio(data.anys ? data.anys.map(a => a.anio) : null).map((year) => (
                  <MenuItem key={year} value={year}>
                    {year}
                  </MenuItem>
                ))}
              </Select>
            </Grid>

            {/* Tarjetas resumen superior y contenido envuelto en un único Grid container sin width: 100% */}
            <Grid container spacing={3} sx={{ marginTop: '10px' }}>
              <Grid size={{ xs: 12, sm: 3 }}>
                <Paper sx={{ padding: '12px', textAlign: 'center' }}>
                  <Typography variant="subtitle2" color="text.secondary">Saldo Inicial (1 Gen)</Typography>
                  <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#666666', mt: 0.5 }}>
                    {formatEuro(data.saldoInicial || 0)}
                  </Typography>
                </Paper>
              </Grid>

              <Grid size={{ xs: 12, sm: 3 }}>
                <Paper sx={{ padding: '12px', textAlign: 'center' }}>
                  <Typography variant="subtitle2" color="text.secondary">Saldo Final (31 Des)</Typography>
                  <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#3F51B5', mt: 0.5 }}>
                    {formatEuro(data.saldoFinal || 0)}
                  </Typography>
                </Paper>
              </Grid>

              <Grid size={{ xs: 12, sm: 3 }}>
                <Paper sx={{ padding: '12px', textAlign: 'center' }}>
                  <Typography variant="subtitle2" color="text.secondary">Aportat / Retirat</Typography>
                  <Typography variant="h5" sx={{ fontWeight: 'bold', mt: 0.5, color: '#666666' }}>
                    {formatAportatRetirat(data.aportacions || 0, data.retiros || 0)}
                  </Typography>
                </Paper>
              </Grid>

              <Grid size={{ xs: 12, sm: 3 }}>
                <Paper sx={{ padding: '12px', textAlign: 'center' }}>
                  <Typography variant="subtitle2" color="text.secondary">Rendiment Anual</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', mt: 0.5, flexWrap: 'wrap' }}>
                    <Typography variant="h5" sx={{ fontWeight: 'bold', color: colorGP }}>
                      {rendimientoTotal >= 0 ? '+' : ''}{formatEuro(rendimientoTotal)}
                    </Typography>
                    <Box sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      backgroundColor: colorGPBg,
                      color: colorGP,
                      border: `1px solid ${rendimientoTotal >= 0 ? '#A5D6A7' : '#FFCDD2'}`,
                      borderRadius: '12px',
                      px: 1,
                      py: 0.2,
                      fontSize: '0.85rem',
                      fontWeight: 'bold',
                    }}>
                      {percentatgeRetorn >= 0 ? '+' : ''}{formatPct(percentatgeRetorn)}
                    </Box>
                  </Box>
                </Paper>
              </Grid>

              {/* Gráfico de evolución */}
              <Grid size={12} sx={{ mt: 1 }}>
                <HistoricInversioChart 
                  assets={assets} 
                  isDaily={false} 
                  isWeekly={true} 
                  selectedAssetId={selectedAssetId} 
                />
              </Grid>

              {/* Pestañas de detalle */}
              <Grid size={12} sx={{ mt: 1 }}>
                <Paper>
                <Tabs value={tabValue} onChange={handleTabChange} indicatorColor="primary" textColor="primary" centered>
                  <Tab label="Actius" />
                  <Tab label="Mesos" />
                </Tabs>

                {/* Pestanya 1: Actius */}
                {tabValue === 0 && (
                  <Box sx={{ p: isMobile ? 1 : 2 }}>
                    {isMobile ? (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                        {assets.map((row) => (
                          <ActiuCardAnual
                            key={row.actiuId}
                            asset={row}
                            isSelected={selectedAssetId === row.actiuId}
                            onClick={() => handleRowClick(row.actiuId)}
                          />
                        ))}
                        {assets.length === 0 && (
                          <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 2 }}>
                            No hi ha actius registrats
                          </Typography>
                        )}
                      </Box>
                    ) : (
                      <TableContainer>
                        <Table>
                          <TableHead>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 'bold' }}>Nom actiu</TableCell>
                              <TableCell align="right" sx={{ fontWeight: 'bold' }}>Saldo Final</TableCell>
                              <TableCell align="center" sx={{ fontWeight: 'bold' }}>Aportat / Retirat</TableCell>
                              <TableCell align="right" sx={{ fontWeight: 'bold' }}>Guany / Pèrdua</TableCell>
                              <TableCell align="right" sx={{ fontWeight: 'bold' }}>Rendiment</TableCell>
                              <TableCell align="right" sx={{ fontWeight: 'bold' }}>Rentabilitat (%)</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {assets.map((row) => {
                              const gp = (row.saldoFinal || 0) - (row.saldoInicial || 0);
                              const gpColor = gp >= 0 ? '#4CAF50' : '#EF4444';
                              const rendColor = (row.rendimientoTotal || 0) >= 0 ? '#4CAF50' : '#EF4444';
                              const retVal = row.percentatgeRetorn !== undefined && row.percentatgeRetorn !== null
                                ? row.percentatgeRetorn
                                : null;
                              const retColor = (retVal || 0) >= 0 ? '#4CAF50' : '#EF4444';
                              const isSelected = selectedAssetId === row.actiuId;
                              return (
                                <TableRow
                                  key={row.actiuId}
                                  hover
                                  onClick={() => handleRowClick(row.actiuId)}
                                  sx={{
                                    cursor: 'pointer',
                                    backgroundColor: isSelected ? 'rgba(63, 81, 181, 0.08)' : 'inherit',
                                    '&:hover': {
                                      backgroundColor: isSelected ? 'rgba(63, 81, 181, 0.12) !important' : 'rgba(0, 0, 0, 0.04)',
                                    }
                                  }}
                                >
                                  <TableCell sx={{ fontWeight: isSelected ? 'bold' : 'normal' }}>
                                    {row.nom}
                                  </TableCell>
                                  <TableCell align="right" sx={{ fontWeight: 'bold' }}>{formatEuro(row.saldoFinal)}</TableCell>
                                  <TableCell align="center">
                                    {formatAportatRetirat(row.aportacions, row.retiros)}
                                  </TableCell>
                                  <TableCell align="right" sx={{ color: gpColor, fontWeight: 'bold' }}>
                                    {gp >= 0 ? '+' : ''}{formatEuro(gp)}
                                  </TableCell>
                                  <TableCell align="right" sx={{ color: rendColor, fontWeight: 'bold' }}>
                                    {(row.rendimientoTotal || 0) >= 0 ? '+' : ''}{formatEuro(row.rendimientoTotal)}
                                  </TableCell>
                                  <TableCell align="right" sx={{ color: retColor, fontWeight: 'bold' }}>
                                    {retVal !== null ? `${retVal >= 0 ? '+' : ''}${formatPct(retVal)}` : '-'}
                                  </TableCell>
                                </TableRow>
                              );
                            })}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    )}
                  </Box>
                )}

                {/* Pestanya 2: Mesos */}
                {tabValue === 1 && (
                  <Box sx={{ p: isMobile ? 1 : 2 }}>
                    {isMobile ? (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                        {months.map((row) => (
                          <MesCardAnual
                            key={row.mes}
                            month={row}
                            onClick={() => navigate(`/${userapp}/inversio/${anyo}/mes/${row.mes}`)}
                          />
                        ))}
                        {months.length === 0 && (
                          <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 2 }}>
                            No hi ha dades mensuals registrades
                          </Typography>
                        )}
                      </Box>
                    ) : (
                      <TableContainer>
                        <Table>
                          <TableHead>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 'bold' }}>Mes</TableCell>
                              <TableCell align="right" sx={{ fontWeight: 'bold' }}>Saldo Final</TableCell>
                              <TableCell align="center" sx={{ fontWeight: 'bold' }}>Aportat / Retirat</TableCell>
                              <TableCell align="right" sx={{ fontWeight: 'bold' }}>Guany / Pèrdua</TableCell>
                              <TableCell align="right" sx={{ fontWeight: 'bold' }}>Rendiment</TableCell>
                              <TableCell align="right" sx={{ fontWeight: 'bold' }}>Rentabilitat (%)</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {months.map((row) => {
                              const gp = (row.saldoFinal || 0) - (row.saldoInicial || 0);
                              const gpColor = gp >= 0 ? '#4CAF50' : '#EF4444';
                              const rendColor = (row.rendimientoTotal || 0) >= 0 ? '#4CAF50' : '#EF4444';
                              const retVal = row.percentatgeRetorn !== undefined && row.percentatgeRetorn !== null
                                ? row.percentatgeRetorn
                                : null;
                              const retColor = (retVal || 0) >= 0 ? '#4CAF50' : '#EF4444';
                              return (
                                <TableRow
                                  key={row.mes}
                                  hover
                                  onClick={() => navigate(`/${userapp}/inversio/${anyo}/mes/${row.mes}`)}
                                  sx={{ cursor: 'pointer' }}
                                >
                                  <TableCell sx={{ color: '#3F51B5', fontWeight: 'bold' }}>
                                    {monthName(row.mes)}
                                  </TableCell>
                                  <TableCell align="right" sx={{ fontWeight: 'bold' }}>{formatEuro(row.saldoFinal)}</TableCell>
                                  <TableCell align="center">
                                    {formatAportatRetirat(row.aportacions, row.retiros)}
                                  </TableCell>
                                  <TableCell align="right" sx={{ color: gpColor, fontWeight: 'bold' }}>
                                    {gp >= 0 ? '+' : ''}{formatEuro(gp)}
                                  </TableCell>
                                  <TableCell align="right" sx={{ color: rendColor, fontWeight: 'bold' }}>
                                    {(row.rendimientoTotal || 0) >= 0 ? '+' : ''}{formatEuro(row.rendimientoTotal)}
                                  </TableCell>
                                  <TableCell align="right" sx={{ color: retColor, fontWeight: 'bold' }}>
                                    {retVal !== null ? `${retVal >= 0 ? '+' : ''}${formatPct(retVal)}` : '-'}
                                  </TableCell>
                                </TableRow>
                              );
                            })}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    )}
                  </Box>
                )}
              </Paper>
            </Grid>
          </Grid>
        </>
      );
    }}
    </DataLoader>
  );
};

export default DashboardInversioAnual;
